import React from "react";
import {
  Zap,
  Smartphone,
  Bell,
  Lock,
  ShieldCheck,
  Eye,
  Download as DownloadIcon,
  ArrowRight,
} from "lucide-react";
import SeoHead from "../components/SeoHead";

const APK_URL = "/dl/android/paysafi.apk";

const features = [
  {
    icon: Zap,
    title: "Auto-record payments",
    body: "Paysafi reads the M-Pesa and bank notifications that land on your phone and records each payment against the right tenant, the moment it happens.",
  },
  {
    icon: Smartphone,
    title: "Built for your phone",
    body: "The same tenants, payments, utilities, and history from the web app, optimised for one-handed use on your phone.",
  },
  {
    icon: Bell,
    title: "Push alerts for review",
    body: "When an auto-captured payment doesn\u2019t match a tenant, Paysafi pushes a notification so you can assign it in one tap.",
  },
];

const steps = [
  {
    num: 1,
    title: "Tap \u201cDownload Paysafi\u201d below",
    body: "Your browser will download the APK to your phone.",
  },
  {
    num: 2,
    title: "Allow installs from your browser",
    body: "Android only asks the first time. Future updates install without this step.",
  },
  {
    num: 3,
    title: "Tap the file and confirm install",
    body: "You\u2019ll see the Paysafi icon on your home screen a moment later.",
  },
];

const trustBadges = [
  { icon: Lock, label: "Encrypted in transit" },
  { icon: ShieldCheck, label: "Signed with release key" },
  { icon: Eye, label: "Minimum permissions" },
];

export default function Download() {
  return (
    <>
      <SeoHead
        title="Paysafi for Android"
        description="Download Paysafi for Android. Automatically records rent payments from M-Pesa and bank notifications \u2014 no typing, no forwarding, no forgetting."
        path="/download"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "MobileApplication",
          "name": "Paysafi",
          "operatingSystem": "ANDROID",
          "applicationCategory": "FinanceApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD",
          },
          "downloadUrl": "https://paysafi.com/dl/android/paysafi.apk",
        }}
      />
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 space-y-20">
      {/* Hero */}
      <section className="text-center space-y-6">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
          Paysafi for Android
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-400">
          Download Paysafi directly to your Android phone. Paysafi automatically
          records rent payments the moment M-Pesa or your bank notifies you. No
          typing. No forwarding. No forgetting.
        </p>
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <a
            href={APK_URL}
            className="btn-primary inline-flex items-center gap-2 px-8 py-3 text-base font-semibold"
          >
            <DownloadIcon className="h-5 w-5" />
            Download Paysafi (APK)
          </a>
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          v2.0.0 &middot; Requires Android 7.0 or later
        </p>
      </section>

      {/* Features */}
      <section>
        <h2 className="text-center text-2xl font-semibold text-gray-900 dark:text-white mb-8">
          What Paysafi for Android adds
        </h2>
        <div className="grid gap-6 sm:grid-cols-3">
          {features.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center space-y-3"
            >
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                <f.icon className="h-6 w-6" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {f.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How to install */}
      <section>
        <h2 className="text-center text-2xl font-semibold text-gray-900 dark:text-white mb-8">
          How to install Paysafi on Android
        </h2>
        <div className="grid gap-6 sm:grid-cols-3">
          {steps.map((s) => (
            <div
              key={s.num}
              className="flex flex-col items-center text-center space-y-3"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-white font-bold text-lg">
                {s.num}
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {s.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {s.body}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <a
            href={APK_URL}
            className="btn-primary inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold"
          >
            <DownloadIcon className="h-4 w-4" />
            Download Paysafi (APK)
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Trust */}
      <section className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-8 space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Is it safe?
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          Yes. The Paysafi APK is signed with the same key as the Google Play
          version, so Android will verify it cryptographically before
          installing. The app only reads notifications from M-Pesa and your bank
          apps; it does not read your SMS, contacts, photos, or personal
          messages. All tenant data is encrypted in transit to Paysafi&rsquo;s
          servers and is never sold or shared.
        </p>
        <div className="flex flex-wrap gap-6 pt-2">
          {trustBadges.map((b) => (
            <div
              key={b.label}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
            >
              <b.icon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              {b.label}
            </div>
          ))}
        </div>
      </section>
    </div>
    </>
  );
}
